import { useEffect, useRef } from "react";

/**
 * Olli core: a glossy liquid orb rendered with a WebGL ray-marched shader.
 * A sphere is displaced by slow flowing fields, shaded with the Olli palette
 * (sky-deep body, sky mids, sun bands), fresnel rim, soft inner glow and a sharp
 * specular, so it reads as a solid, polished 3D object. (Design: components/marketing/ParticleOrb.tsx)
 */

const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }`;

const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform float uAmp;
uniform float uVoice;
uniform float uDark;
uniform vec3 cDeep;
uniform vec3 cSky;
uniform vec3 cSun;
uniform vec3 cInk;

float field(vec3 p, float t){
  float a = sin(p.x*1.9 + t*0.9) * sin(p.y*2.3 - t*0.7) * sin(p.z*1.7 + t*0.5);
  float b = sin(p.x*3.7 - p.z*2.1 + t*1.3) * sin(p.y*3.1 + t*0.6);
  float c = sin(length(p.xy)*7.0 - t*4.0) * uVoice;
  return a*0.66 + b*0.2 + c*0.07;
}

float sdf(vec3 p, float t){
  float r = 1.0 + field(p, t) * uAmp;
  return (length(p) - r) * 0.62;
}

vec3 normalAt(vec3 p, float t){
  vec2 e = vec2(0.0025, 0.0);
  return normalize(vec3(
    sdf(p+e.xyy,t) - sdf(p-e.xyy,t),
    sdf(p+e.yxy,t) - sdf(p-e.yxy,t),
    sdf(p+e.yyx,t) - sdf(p-e.yyx,t)));
}

mat3 rotY(float a){ float c=cos(a), s=sin(a); return mat3(c,0.0,-s, 0.0,1.0,0.0, s,0.0,c); }
mat3 rotX(float a){ float c=cos(a), s=sin(a); return mat3(1.0,0.0,0.0, 0.0,c,s, 0.0,-s,c); }

void main(){
  vec2 uv = (gl_FragCoord.xy - 0.5*uRes) / (0.5*min(uRes.x, uRes.y));
  float t = uTime;
  vec3 ro = vec3(0.0, 0.0, 3.35);
  vec3 rd = normalize(vec3(uv*0.6, -1.0));
  mat3 R = rotY(t*0.22) * rotX(0.25 + sin(t*0.3)*0.08);

  float d = 0.0;
  float minD = 1e3;
  bool hit = false;
  vec3 p;
  for(int i=0; i<72; i++){
    p = R * (ro + rd*d);
    float h = sdf(p, t);
    minD = min(minD, h);
    if(h < 0.0012){ hit = true; break; }
    d += h;
    if(d > 6.0) break;
  }

  if(!hit){
    // soft halo / contact glow around the orb
    float r = length(uv);
    float halo = exp(-pow(max(r - 0.62, 0.0) * 5.0, 2.0));
    vec3 hc = mix(cSky, cSun, 0.35);
    float a = halo * (uDark > 0.5 ? 0.3 : 0.14) * smoothstep(0.98, 0.72, r);
    gl_FragColor = vec4(hc * a, a);
    return;
  }

  vec3 n = normalAt(p, t);
  vec3 v = normalize(R * -rd);
  vec3 L = normalize(R * vec3(-0.55, 0.75, 0.6));
  vec3 L2 = normalize(R * vec3(0.7, -0.3, 0.5));

  float f = field(p, t);
  float fres = pow(1.0 - max(dot(n, v), 0.0), 2.6);

  // soft flowing colour fields, like light moving through glass
  float flowA = 0.5 + 0.5*sin(dot(n, vec3(0.9, 1.6, 0.4))*1.5 + t*0.55 + f*3.0);
  float flowB = 0.5 + 0.5*sin(dot(n, vec3(-1.3, 0.4, 1.1))*1.8 - t*0.42 + f*2.0);
  vec3 body = mix(cDeep, cSky, smoothstep(-0.7, 1.0, n.y*0.8 + flowB*0.6 - 0.35));
  body = mix(body, mix(cSun, vec3(1.0), 0.08), smoothstep(0.55, 1.0, flowA) * 0.55);
  body = mix(body, mix(cSky, vec3(1.0), 0.35), smoothstep(0.6, 1.0, flowB) * 0.25);

  float wrap = max(dot(n, L)*0.5 + 0.5, 0.0);
  vec3 col = body * (0.55 + 0.6*wrap);
  col += cSky * max(dot(n, L2), 0.0) * 0.18;                       // cool fill
  col += mix(vec3(1.0), cSun, flowA*0.6) * fres * 0.55;            // glassy rim
  vec3 h = normalize(L + v);
  col += vec3(1.0) * pow(max(dot(n, h), 0.0), 120.0) * 0.9;        // sharp specular
  col += vec3(1.0) * pow(max(dot(n, h), 0.0), 18.0) * 0.14;        // sheen
  col += cSun * pow(flowA, 3.0) * (0.08 + 0.3*(uAmp - 0.16));      // inner glow when thinking
  col = clamp(col, 0.0, 1.0);
  col = mix(col, col*col*(3.0-2.0*col), 0.25);                     // gentle contrast curve
  col = clamp(col, 0.0, 1.0);
  gl_FragColor = vec4(col, 1.0);
}`;

function hexToRgb(value, fallback) {
  const src = (value.trim() || fallback).replace("#", "");
  const full = src.length === 3 ? src.split("").map((c) => c + c).join("") : src;
  const n = parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(n)) return hexToRgb(fallback, "#000000");
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

export function ParticleOrb({ size = 380, state = "idle", surface = "light" }) {
  const canvasRef = useRef(null);
  const fallbackRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: true });
    const vs = gl && compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = gl && compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const program = gl && vs && fs ? gl.createProgram() : null;
    if (!gl || !vs || !fs || !program) {
      if (fallbackRef.current) fallbackRef.current.style.display = "block";
      canvas.style.display = "none";
      return;
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      if (fallbackRef.current) fallbackRef.current.style.display = "block";
      canvas.style.display = "none";
      return;
    }
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(program, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const u = (name) => gl.getUniformLocation(program, name);
    const uRes = u("uRes"), uTime = u("uTime"), uAmp = u("uAmp"), uVoice = u("uVoice"), uDark = u("uDark");
    const css = getComputedStyle(document.documentElement);
    gl.uniform3fv(u("cDeep"), hexToRgb(css.getPropertyValue("--color-sky-deep"), "#2F5F8A"));
    gl.uniform3fv(u("cSky"), hexToRgb(css.getPropertyValue("--color-sky"), "#82ADD4"));
    gl.uniform3fv(u("cSun"), hexToRgb(css.getPropertyValue("--color-sun"), "#DDFD2C"));
    gl.uniform3fv(u("cInk"), hexToRgb(css.getPropertyValue("--color-ink"), "#000000"));
    gl.uniform1f(uDark, surface === "dark" ? 1 : 0);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let last = 0;
    let clock = 3;
    let amp = 0.16;
    let voice = 0;
    let visible = true;
    let disposed = false;

    const resize = () => {
      const b = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round((b.width || size) * ratio));
      canvas.height = Math.max(1, Math.round((b.height || size) * ratio));
      gl.viewport(0, 0, canvas.width, canvas.height);
      if (reduced.matches) render(0);
    };

    const render = (time) => {
      if (disposed) return;
      const dt = last ? Math.min((time - last) / 1000, 0.05) : 0;
      last = time;
      const s = stateRef.current;
      const targetAmp = s === "thinking" ? 0.3 : s === "listening" ? 0.22 : 0.16;
      amp += (targetAmp - amp) * Math.min(1, dt * 2.5);
      voice += ((s === "listening" ? 1 : 0) - voice) * Math.min(1, dt * 3);
      clock += dt * (0.55 + (amp - 0.16) * 4);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, reduced.matches ? 3 : clock);
      gl.uniform1f(uAmp, amp);
      gl.uniform1f(uVoice, voice);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };

    const loop = (time) => {
      render(time);
      if (!disposed && !reduced.matches && visible && !document.hidden) frame = requestAnimationFrame(loop);
    };
    const start = () => {
      cancelAnimationFrame(frame);
      last = 0;
      if (reduced.matches) render(0);
      else if (visible && !document.hidden) frame = requestAnimationFrame(loop);
    };
    const io = new IntersectionObserver((entries) => {
      visible = entries.some((e) => e.isIntersecting);
      start();
    });
    io.observe(canvas);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    document.addEventListener("visibilitychange", start);
    reduced.addEventListener("change", start);
    resize();
    start();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      io.disconnect();
      ro.disconnect();
      document.removeEventListener("visibilitychange", start);
      reduced.removeEventListener("change", start);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [size, surface]);

  return (
    <div className="pointer-events-none relative" style={{ width: size, height: size }} aria-hidden="true">
      <canvas ref={canvasRef} className="block h-full w-full" />
      <div
        ref={fallbackRef}
        className="absolute inset-[12%] hidden rounded-full shadow-lift"
        style={{ background: "radial-gradient(circle at 32% 28%, var(--color-paper) 0%, var(--color-sun) 18%, var(--color-sky) 52%, var(--color-sky-deep) 100%)" }}
      />
    </div>
  );
}
